import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Trophy,
  Phone,
  MapPin,
  Mail,
  ShieldCheck,
  CreditCard,
  Clock,
  CheckCircle2,
  ArrowRight,
} from "lucide-react";

const api = import.meta.env.VITE_API_URL;

function Profile() {
  const navigate = useNavigate();

  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const id = localStorage.getItem("player");

    if (!id) {
      navigate("/");
      return;
    }

    const fetchData = async () => {
      try {
        const response = await fetch(`${api}/api/players/${id}`);

        if (!response.ok) {
          throw new Error("Failed to fetch player");
        }

        const data = await response.json();

        setUserData(data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  /*
  |--------------------------------------------------------------------------
  | Loading
  |--------------------------------------------------------------------------
  */

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-950 via-red-950 to-black">
        <div className="text-center text-white">
          <Trophy className="mx-auto mb-4 h-10 w-10 animate-pulse text-yellow-400" />

          <p className="text-xl font-semibold">
            Loading Profile...
          </p>
        </div>
      </div>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | No Data
  |--------------------------------------------------------------------------
  */

  if (!userData) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-950 via-red-950 to-black px-4 text-white">
        <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center backdrop-blur-md">
          <p className="text-xl font-semibold">
            No player data available
          </p>

          <button
            onClick={() => navigate("/")}
            className="mt-5 rounded-xl bg-gradient-to-r from-red-600 to-orange-500 px-6 py-3 font-bold"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | Helpers
  |--------------------------------------------------------------------------
  */

  const formatName = (name) => {
    return (
      name
        ?.split(" ")
        .map(
          (word) =>
            word.charAt(0).toUpperCase() +
            word.slice(1).toLowerCase()
        )
        .join(" ") || "Player"
    );
  };

  const getRole = (role) => {
    switch (role) {
      case 1:
        return "Batsman";
      case 2:
        return "Wicket-Keeper";
      case 3:
        return "Batsman-Allrounder";
      case 4:
        return "Bowler-Allrounder";
      case 5:
        return "Bowler";
      default:
        return "Unknown";
    }
  };

  const getBattingStyle = (style) => {
    if (style === 1) return "Right-Handed";
    if (style === 2) return "Left-Handed";

    return "Unknown";
  };

  /*
  |--------------------------------------------------------------------------
  | Payment
  |--------------------------------------------------------------------------
  */

  const handlePayment = () => {
    navigate(`/payment/${userData.id}`, {
      state: {
        playerName: userData.name,
      },
    });
  };

  /*
  |--------------------------------------------------------------------------
  | UI
  |--------------------------------------------------------------------------
  */

  return (
    <main className="relative min-h-screen overflow-hidden bg-gradient-to-br from-gray-950 via-red-950 to-black px-4 py-10 text-white sm:px-6 md:py-14">

      {/* Background Effects */}

      <div className="pointer-events-none absolute -left-32 top-20 h-80 w-80 rounded-full bg-red-600/20 blur-3xl" />

      <div className="pointer-events-none absolute -right-32 bottom-20 h-80 w-80 rounded-full bg-orange-500/10 blur-3xl" />

      <div className="relative z-10 mx-auto w-full max-w-3xl">

        {/* Header */}

        <div className="mb-8 text-center">

          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl border border-yellow-400/20 bg-yellow-400/10">
            <Trophy className="h-8 w-8 text-yellow-400" />
          </div>

          <p className="mt-4 text-sm font-bold uppercase tracking-[0.3em] text-orange-400">
            BPL Season 2
          </p>

          <h1 className="mt-2 text-3xl font-black sm:text-4xl">
            Player Profile
          </h1>

          <p className="mt-2 text-sm text-gray-400">
            Badgaw Premier League
          </p>

        </div>

        {/* Profile Card */}

        <div className="overflow-hidden rounded-3xl border border-white/10 bg-white/[0.06] shadow-2xl backdrop-blur-xl">

          {/* Profile Header */}

          <div className="border-b border-white/10 bg-gradient-to-r from-red-700/40 to-orange-500/20 p-6 sm:p-8">

            <div className="flex flex-col items-center">

              {/* Profile Image */}

              <div className="relative">

                <div className="absolute -inset-1 rounded-full bg-gradient-to-r from-yellow-400 via-orange-500 to-red-600 opacity-70 blur-sm" />

                <img
                  src={
                    userData.photo ||
                    "https://via.placeholder.com/150"
                  }
                  alt={formatName(userData.name)}
                  className="relative h-32 w-32 rounded-full border-4 border-gray-950 object-cover shadow-xl sm:h-36 sm:w-36"
                />

              </div>

              {/* Name */}

              <h2 className="mt-5 text-2xl font-black sm:text-3xl">
                {formatName(userData.name)}

                {userData.islocal !== 1 && (
                  <span className="ml-2">
                    ✈️
                  </span>
                )}
              </h2>

              {/* Email */}

              <div className="mt-2 flex items-center gap-2 text-sm text-gray-400">
                <Mail className="h-4 w-4" />

                {userData.email || "No Email Available"}
              </div>

              {/* Player ID */}

              <div className="mt-3 rounded-full border border-orange-400/20 bg-orange-400/10 px-4 py-1.5 text-xs font-bold text-orange-300">
                Player ID: #{userData.id}
              </div>

            </div>

          </div>

          {/* Information */}

          <div className="p-6 sm:p-8">

            <div className="grid gap-4 sm:grid-cols-2">

              {/* Phone */}

              <div className="rounded-2xl border border-white/10 bg-black/20 p-5">

                <div className="flex items-center gap-3">

                  <div className="rounded-xl bg-blue-500/10 p-2.5">
                    <Phone className="h-5 w-5 text-blue-400" />
                  </div>

                  <div>
                    <p className="text-xs uppercase tracking-wider text-gray-500">
                      Phone
                    </p>

                    <p className="mt-1 font-semibold text-white">
                      {userData.phone || "N/A"}
                    </p>
                  </div>

                </div>

              </div>

              {/* Address */}

              <div className="rounded-2xl border border-white/10 bg-black/20 p-5">

                <div className="flex items-center gap-3">

                  <div className="rounded-xl bg-red-500/10 p-2.5">
                    <MapPin className="h-5 w-5 text-red-400" />
                  </div>

                  <div>
                    <p className="text-xs uppercase tracking-wider text-gray-500">
                      Address
                    </p>

                    <p className="mt-1 font-semibold text-white">
                      {formatName(userData.address) || "N/A"}
                    </p>
                  </div>

                </div>

              </div>

              {/* Batting Style */}

              <div className="rounded-2xl border border-white/10 bg-black/20 p-5">

                <div className="flex items-center gap-3">

                  <div className="rounded-xl bg-green-500/10 p-2.5">
                    <ShieldCheck className="h-5 w-5 text-green-400" />
                  </div>

                  <div>
                    <p className="text-xs uppercase tracking-wider text-gray-500">
                      Batting Style
                    </p>

                    <p className="mt-1 font-semibold text-white">
                      {getBattingStyle(userData.style)}
                    </p>
                  </div>

                </div>

              </div>

              {/* Role */}

              <div className="rounded-2xl border border-white/10 bg-black/20 p-5">

                <div className="flex items-center gap-3">

                  <div className="rounded-xl bg-orange-500/10 p-2.5">
                    <Trophy className="h-5 w-5 text-orange-400" />
                  </div>

                  <div>
                    <p className="text-xs uppercase tracking-wider text-gray-500">
                      Playing Role
                    </p>

                    <p className="mt-1 font-semibold text-white">
                      {getRole(userData.role)}
                    </p>
                  </div>

                </div>

              </div>

            </div>

            {/* Status */}

            <div className="mt-6">

              {userData.status === 1 ? (

                /* Approved */

                <div className="rounded-2xl border border-green-500/20 bg-green-500/5 p-5">

                  <div className="flex items-center gap-4">

                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-green-500/10">
                      <CheckCircle2 className="h-6 w-6 text-green-400" />
                    </div>

                    <div>

                      <p className="text-xs font-bold uppercase tracking-wider text-green-400">
                        Registration Status
                      </p>

                      <h3 className="mt-1 text-xl font-black text-white">
                        Approved
                      </h3>

                      <p className="mt-1 text-sm text-gray-400">
                        Your BPL Season 2 registration has been approved.
                      </p>

                    </div>

                  </div>

                </div>

              ) : (

                /* Pending */

                <div className="rounded-2xl border border-yellow-400/20 bg-yellow-400/5 p-5">

                  <div className="flex items-start gap-4">

                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-yellow-400/10">
                      <Clock className="h-6 w-6 text-yellow-400" />
                    </div>

                    <div className="flex-1">

                      <p className="text-xs font-bold uppercase tracking-wider text-yellow-400">
                        Registration Status
                      </p>

                      <h3 className="mt-1 text-xl font-black text-white">
                        Payment / Approval Pending
                      </h3>

                      <p className="mt-1 text-sm leading-6 text-gray-400">
                        Complete the ₹150 registration payment.
                        Your registration will be approved after the
                        BPL admin verifies your payment.
                      </p>

                      {/* Payment Button */}

                      <button
                        type="button"
                        onClick={handlePayment}
                        className="group mt-5 flex w-full items-center justify-center gap-3 rounded-xl bg-gradient-to-r from-red-600 to-orange-500 py-3.5 font-bold text-white shadow-lg shadow-red-950/30 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl sm:w-auto sm:px-7"
                      >

                        <CreditCard className="h-5 w-5" />

                        Pay ₹150 Registration Fee

                        <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />

                      </button>

                    </div>

                  </div>

                </div>

              )}

            </div>

          </div>

        </div>

      </div>

    </main>
  );
}

export default Profile;